import { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { useStyle } from '../context/StyleContext';
import { getProjectDefaultVideoRatio } from '../utils/projectRuntime';

/**
 * StoryboardTable 核心状态管理 Hook
 * @param {Array|Object} options - 初始分镜数据或配置对象
 * @returns {Object} 状态和方法集合
 */
export function useStoryboardState(options = []) {
  // 支持两种调用方式: useStoryboardState(rows) 或 useStoryboardState({ initialData: rows })
  const rawInitialRows = Array.isArray(options) ? options : options?.initialData;
  // 确保 initialRows 始终是数组
  const initialRows = Array.isArray(rawInitialRows) ? rawInitialRows : [];

  const onDataChange = Array.isArray(options) ? null : options?.onDataChange;
  const projectId = Array.isArray(options) ? null : options?.projectId;

  // 使用全局 Style Context
  const { currentStyle, setCurrentStyle: updateStyle } = useStyle();

  // ==================== State ====================
  
  /** 分镜数据数组 - 确保始终是数组 */
  const [rows, setRowsState] = useState(() => withStableRowKeys(Array.isArray(initialRows) ? initialRows : []));
  
  /** 视图模式: 'list' | 'grid' | 'flow' | 'gallery' */
  const [viewMode, setViewMode] = useState('list');
  
  /** 预览图片 URL */
  const [previewImage, setPreviewImage] = useState(null);
  
  /** 复制的提示词 ID（用于显示"已复制"状态） */
  const [copiedId, setCopiedId] = useState(null);
  
  /** 重绘模态框开关 */
  const [redrawModalOpen, setRedrawModalOpen] = useState(false);
  
  /** 重绘目标索引 */
  const [redrawTargetIndex, setRedrawTargetIndex] = useState(null);
  
  /** 保存中状态 */
  const [isSaving, setIsSaving] = useState(false);
  
  /** 当前画幅比例: '16:9' | '9:16' | '4:3' | '2.35:1' */
  const [currentRatio, setCurrentRatio] = useState(() => {
    if (typeof window !== 'undefined') {
      return getProjectDefaultVideoRatio(projectId, localStorage.getItem('video_ratio') || '9:16');
    }
    return '9:16';
  });

  useEffect(() => {
    if (!projectId || typeof window === 'undefined') return undefined;
    const syncProjectRatio = (event) => {
      const eventProjectId = event?.detail?.projectId;
      if (eventProjectId && String(eventProjectId) !== String(projectId)) return;
      setCurrentRatio(getProjectDefaultVideoRatio(projectId, localStorage.getItem('video_ratio') || '9:16'));
    };
    syncProjectRatio();
    window.addEventListener('project-runtime-profile-change', syncProjectRatio);
    return () => window.removeEventListener('project-runtime-profile-change', syncProjectRatio);
  }, [projectId]);
  
  /** 显示网格向导 */
  const [showGridWizard, setShowGridWizard] = useState(false);
  
  /** 打开视频设置的行索引 */
  const [openVideoSettingsIndex, setOpenVideoSettingsIndex] = useState(null);
  
  /** 网格视图悬停索引 */
  const [hoveredGridIndex, setHoveredGridIndex] = useState(null);
  
  /** Gallery 视图选中的镜头索引 */
  const [selectedShotIndex, setSelectedShotIndex] = useState(0);
  
  /** 音频模态框状态 { visible, shotIndex, initialText } */
  const [audioModal, setAudioModal] = useState({
    visible: false,
    shotIndex: null,
    initialText: ''
  });
  
  /** 批量操作类型 */
  const [batchAction, setBatchAction] = useState(null);
  
  /** 批量进度 { current, total } */
  const [batchProgress, setBatchProgress] = useState({ current: 0, total: 0 });
  
  /** 批量操作目标索引 */
  const [batchTargetIndex, setBatchTargetIndex] = useState(null);

  // ==================== Refs ====================
  
  /** 文件输入引用集合 */
  const fileInputRefs = useRef({});
  
  /** 自动保存定时器引用 */
  const autoSaveTimerRef = useRef(null);

  /** 轮询间隔引用集合 */
  const pollIntervalsRef = useRef({});

  /** 初始数据身份签名（用于区分真正换了一批分镜，而不是输入时父组件回灌） */
  const initialRowsSignatureRef = useRef(undefined);

  /** 上一次 rows 的引用（用于比较变化） */
  const prevRowsRef = useRef([]);

  /** 防抖定时器 ref */
  const debounceTimerRef = useRef(null);

  // ==================== Computed ====================
  
  /** 
   * 全局锁定状态 - 检查是否有行在生成中
   * 用于禁用某些操作以防止冲突
   */
  const isGlobalLocked = useMemo(() => {
    return Array.isArray(rows) && rows.some(row => row?.status === 'generating' || row?.isGenerating);
  }, [rows]);

  // ==================== Handlers ====================
  
  /**
   * 修改行数据
   * @param {number} index - 行索引
   * @param {string} field - 字段名
   * @param {any} value - 新值
   */
  const handleChange = useCallback((index, field, value) => {
    setRowsState(prevRows => {
      if (!Array.isArray(prevRows)) {
        console.warn('rows is not an array');
        return [];
      }
      if (index < 0 || index >= prevRows.length) {
        console.warn(`Invalid row index: ${index}`);
        return prevRows;
      }
      const newRows = [...prevRows];
      newRows[index] = cloneRowWithStableKey(newRows[index], { [field]: value });
      return withStableRowKeys(newRows, prevRows);
    });
  }, []);
  
  /**
   * 插入新行
   * @param {number} index - 插入位置的索引
   */
  const handleInsertRow = useCallback((index, initialData = {}) => {
    setRowsState(prevRows => {
      if (!Array.isArray(prevRows)) return [];
      const newRows = [...prevRows];
      const newRow = {
        id: -Date.now(),
        shot_number: index + 1,  // 后端字段
        shotNumber: index + 1,   // 前端字段
        ...getDefaultRowData(currentRatio),
        ...initialData,
      };
      newRows.splice(index, 0, newRow);
      return renumberRows(newRows, prevRows);
    });
  }, [currentRatio]);
  
  /**
   * 删除行
   * @param {number} index - 要删除的行索引
   */
  const handleDeleteRow = useCallback((index) => {
    setRowsState(prevRows => {
      if (!Array.isArray(prevRows)) {
        console.warn('rows is not an array');
        return [];
      }
      if (index < 0 || index >= prevRows.length) {
        console.warn(`Invalid row index: ${index}`);
        return prevRows;
      }
      const newRows = prevRows.filter((_, i) => i !== index);
      return renumberRows(newRows, prevRows);
    });
  }, []);
  
  /**
   * 批量更新行数据
   * @param {Array} newRows - 新的行数据数组
   */
  const handleUpdateRows = useCallback((newRows) => {
    setRowsState(prevRows => renumberRows(newRows, prevRows));
  }, []);
  
  /**
   * 清空所有行
   */
  const handleClearRows = useCallback(() => {
    setRowsState([]);
  }, []);
  
  /**
   * 设置复制的提示词 ID（带自动清除）
   * @param {string} id - 提示词 ID
   * @param {number} duration - 显示持续时间（毫秒）
   */
  const setCopiedIdWithTimeout = useCallback((id, duration = 2000) => {
    setCopiedId(id);
    setTimeout(() => {
      setCopiedId(prev => prev === id ? null : prev);
    }, duration);
  }, []);
  
  /**
   * 打开重绘模态框
   * @param {number} index - 目标行索引
   */
  const openRedrawModal = useCallback((index) => {
    setRedrawTargetIndex(index);
    setRedrawModalOpen(true);
  }, []);
  
  /**
   * 关闭重绘模态框
   */
  const closeRedrawModal = useCallback(() => {
    setRedrawModalOpen(false);
    setRedrawTargetIndex(null);
  }, []);
  
  /**
   * 打开音频模态框
   * @param {number} shotIndex - 镜头索引
   * @param {string} initialText - 初始文本
   */
  const openAudioModal = useCallback((shotIndex, initialText = '') => {
    setAudioModal({
      visible: true,
      shotIndex,
      initialText
    });
  }, []);
  
  /**
   * 关闭音频模态框
   */
  const closeAudioModal = useCallback(() => {
    setAudioModal({
      visible: false,
      shotIndex: null,
      initialText: ''
    });
  }, []);
  
  /**
   * 更新画幅比例（同时更新 localStorage）
   * @param {string} ratio - 新的画幅比例
   */
  const updateRatio = useCallback((ratio) => {
    setCurrentRatio(ratio);
    if (typeof window !== 'undefined') {
      localStorage.setItem('video_ratio', ratio);
    }
  }, []);
  
  /**
   * 设置批量操作状态
   * @param {string|null} action - 批量操作类型
   * @param {number} current - 当前进度
   * @param {number} total - 总任务数
   * @param {number|null} targetIndex - 目标索引
   */
  const setBatchOperation = useCallback((action, current = 0, total = 0, targetIndex = null) => {
    setBatchAction(action);
    setBatchProgress({ current, total });
    setBatchTargetIndex(targetIndex);
  }, []);
  
  /**
   * 更新批量进度
   * @param {number} current - 当前进度
   * @param {number} total - 总任务数
   */
  const updateBatchProgress = useCallback((current, total) => {
    setBatchProgress({ current, total });
  }, []);
  
  /**
   * 清除批量操作状态
   */
  const clearBatchOperation = useCallback(() => {
    setBatchAction(null);
    setBatchProgress({ current: 0, total: 0 });
    setBatchTargetIndex(null);
  }, []);
  
  /**
   * 获取指定索引行的文件输入引用
   * @param {number} index - 行索引
   * @returns {React.RefObject}
   */
  const getFileInputRef = useCallback((index) => {
    if (!fileInputRefs.current[index]) {
      fileInputRefs.current[index] = { current: null };
    }
    return fileInputRefs.current[index];
  }, []);
  
  /**
   * 清除自动保存定时器
   */
  const clearAutoSaveTimer = useCallback(() => {
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }
  }, []);
  
  /**
   * 清除所有轮询间隔
   */
  const clearAllPollIntervals = useCallback(() => {
    Object.values(pollIntervalsRef.current).forEach(interval => {
      if (interval) clearInterval(interval);
    });
    pollIntervalsRef.current = {};
  }, []);
  
  /**
   * 设置指定行的轮询间隔
   * @param {string|number} rowId - 行 ID
   * @param {number} intervalId - 间隔 ID
   */
  const setPollInterval = useCallback((rowId, intervalId) => {
    // 先清除旧的
    if (pollIntervalsRef.current[rowId]) {
      clearInterval(pollIntervalsRef.current[rowId]);
    }
    pollIntervalsRef.current[rowId] = intervalId;
  }, []);
  
  /**
   * 清除指定行的轮询间隔
   * @param {string|number} rowId - 行 ID
   */
  const clearPollInterval = useCallback((rowId) => {
    if (pollIntervalsRef.current[rowId]) {
      clearInterval(pollIntervalsRef.current[rowId]);
      delete pollIntervalsRef.current[rowId];
    }
  }, []);

  // ==================== Effects ====================

  // 同步外部数据变化到内部状态（仅当初始数据真正变化时）
  // 注意：不依赖 rows，避免输入时的循环
  useEffect(() => {
    const currentSignature = getRowsIdentitySignature(initialRows);

    // 首次初始化
    if (initialRowsSignatureRef.current === undefined) {
      initialRowsSignatureRef.current = currentSignature;
      // 只有内部为空且外部有数据时才初始化
      if (rows.length === 0 && initialRows.length > 0) {
        setRowsState(withStableRowKeys(initialRows));
      }
      return;
    }

    // 只有换了一批分镜（数量或 id 序列变化）时才同步，避免输入时父组件回灌覆盖光标。
    if (currentSignature !== initialRowsSignatureRef.current) {
      initialRowsSignatureRef.current = currentSignature;
      setRowsState(prevRows => withStableRowKeys(initialRows, prevRows));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialRows]);

  // 同步内部状态变化到外部（防抖，避免频繁更新导致输入卡顿）
  useEffect(() => {
    // 只有当 rows 真正变化时才回调
    const rowsStr = JSON.stringify(rows);
    const prevRowsStr = JSON.stringify(prevRowsRef.current);

    if (rowsStr !== prevRowsStr) {
      prevRowsRef.current = rows;
      
      // 清除之前的防抖定时器
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // 防抖 500ms，避免频繁同步导致输入卡顿
      debounceTimerRef.current = setTimeout(() => {
        if (onDataChange) {
          onDataChange(rows);
        }
      }, 500);
    }

    // 清理定时器
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [rows, onDataChange]);

  // 清理定时器和轮询
  useEffect(() => {
    return () => {
      clearAutoSaveTimer();
      clearAllPollIntervals();
    };
  }, [clearAutoSaveTimer, clearAllPollIntervals]);

  // ==================== Return ====================
  
  return {
    // 状态
    rows,
    viewMode,
    previewImage,
    copiedId,
    redrawModalOpen,
    redrawTargetIndex,
    isSaving,
    currentRatio,
    showGridWizard,
    openVideoSettingsIndex,
    hoveredGridIndex,
    selectedShotIndex,
    audioModal,
    currentStyle,
    batchAction,
    batchProgress,
    batchTargetIndex,
    
    // 计算属性
    isGlobalLocked,
    
    // Refs
    fileInputRefs,
    autoSaveTimerRef,
    pollIntervalsRef,
    
    // Setters - 用 useCallback 包装避免无限循环
    setRows: useCallback((fnOrValue) => {
      setRowsState(prev => {
        const nextRows = typeof fnOrValue === 'function' ? fnOrValue(prev) : fnOrValue;
        return withStableRowKeys(nextRows, prev);
      });
    }, []),
    setViewMode,
    setPreviewImage,
    setCopiedId,
    setRedrawModalOpen,
    setRedrawTargetIndex,
    setIsSaving,
    setCurrentRatio: updateRatio,
    setShowGridWizard,
    setOpenVideoSettingsIndex,
    setHoveredGridIndex,
    setSelectedShotIndex,
    setAudioModal,
    setCurrentStyle: updateStyle,
    setBatchAction,
    setBatchProgress,
    setBatchTargetIndex,
    
    // Handlers
    handleChange,
    handleInsertRow,
    handleDeleteRow,
    handleUpdateRows,
    handleClearRows,
    setCopiedIdWithTimeout,
    openRedrawModal,
    closeRedrawModal,
    openAudioModal,
    closeAudioModal,
    setBatchOperation,
    updateBatchProgress,
    clearBatchOperation,
    
    // Ref helpers
    getFileInputRef,
    clearAutoSaveTimer,
    clearAllPollIntervals,
    setPollInterval,
    clearPollInterval
  };
}

// ==================== Helpers ====================

let storyboardRowKeySeed = 0;

function createRowClientKey(row) {
  const idPart = row?.id != null ? String(row.id) : 'new';
  storyboardRowKeySeed += 1;
  return `storyboard-row-${idPart}-${storyboardRowKeySeed}`;
}

function defineRowClientKey(row, key) {
  if (!row || typeof row !== 'object') return row;
  if (row.__clientKey === key) return row;
  Object.defineProperty(row, '__clientKey', {
    value: key,
    enumerable: false,
    writable: true,
    configurable: true,
  });
  return row;
}

function withStableRowKeys(rows, previousRows = []) {
  if (!Array.isArray(rows)) return [];

  const previousKeysById = new Map();
  const canReuseKeysByIndex = Array.isArray(previousRows) && previousRows.length === rows.length;
  previousRows.forEach((row) => {
    if (row?.id != null && row.__clientKey) {
      previousKeysById.set(String(row.id), row.__clientKey);
    }
  });

  const nextRows = rows.map((row, index) => {
    if (!row || typeof row !== 'object') return row;
    const key = row.__clientKey ||
      (row.id != null ? previousKeysById.get(String(row.id)) : null) ||
      (canReuseKeysByIndex ? previousRows[index]?.__clientKey : null) ||
      createRowClientKey(row);
    return defineRowClientKey(row, key);
  });
  return nextRows.every((row, index) => row === rows[index]) ? rows : nextRows;
}

function cloneRowWithStableKey(row, updates = {}) {
  const key = row?.__clientKey || createRowClientKey(row);
  return defineRowClientKey({ ...row, ...updates }, key);
}

function getRowsIdentitySignature(rows) {
  if (!Array.isArray(rows)) return '0';
  return rows.map((row, index) => {
    if (row?.id != null) return `id:${row.id}`;
    if (row?.shot_number != null) return `shot:${row.shot_number}`;
    return `index:${index}`;
  }).join('|');
}

/**
 * 获取默认行数据
 * @param {string} ratio - 画幅比例
 * @returns {Object}
 */
function getDefaultRowData(ratio) {
  const ratioMap = {
    '16:9': { width: 1920, height: 1080 },
    '9:16': { width: 1080, height: 1920 },
    '4:3': { width: 1440, height: 1080 },
    '2.35:1': { width: 1920, height: 817 }
  };
  
  const dimensions = ratioMap[ratio] || ratioMap['16:9'];
  
  return {
    width: dimensions.width,
    height: dimensions.height,
    ratio: ratio,
    duration: 3,
    transition: 'none',
    cameraMove: 'static',
    director_enabled: false,
    director_context: null,
    ...dimensions
  };
}

/**
 * 重新编号行
 * @param {Array} rows - 行数据数组
 * @returns {Array}
 */
function renumberRows(rows, previousRows = []) {
  if (!Array.isArray(rows)) return [];
  const numberedRows = rows.map((row, index) => cloneRowWithStableKey(row, {
    shot_number: index + 1,  // 后端数据库字段（下划线命名）
    shotNumber: index + 1,   // 前端使用字段（驼峰命名）
    sequence: index + 1
  }));
  return withStableRowKeys(numberedRows, previousRows);
}

export default useStoryboardState;
